import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ONE_MB_IN_BYTES } from '~/constants/file'
import type { Campground } from '~/types/rental-api-aliases'
import type { CampgroundAmenity } from '~/types/campground'

export const AMENITY_LABELS: Partial<Record<CampgroundAmenity, string>> = {
  HasBeach: 'beach',
  HasBoatLaunch: 'boatLaunch',
  HasCableTV: 'cableTv',
  HasDumpStation: 'dumpStation',
  HasFirewood: 'firewood',
  HasFishing: 'fishing',
  HasGeneralStore: 'generalStore',
  HasGrills: 'grills',
  HasHiking: 'hiking',
  HasInternet: 'internet',
  HasLaundryFacilities: 'laundry',
  HasPlayground: 'playground',
  HasPool: 'pool',
  HasPropane: 'propane',
  HasRVStorage: 'rvStorage',
  HasShowers: 'showers',
  HasTables: 'tables',
  HasToilets: 'toilets',
  HasTrails: 'trails',
  IsPetFriendly: 'petFriendly',

  HasWaterHookup: 'waterHookup',
  HasSewerHookup: 'sewerHookup',
  HasTentsSites: 'tentSites',
  IsBigRigFriendly: 'bigRigs',
  HasPullThrusSitesAvailable: 'pullThroughSites',
  HasEletricalHookup: 'electricHookup',
  Has15AmpHookup: 'electric15AmpHookup',
  Has20AmpHookup: 'electric20AmpHookup',
  Has30AmpHookup: 'electric30AmpHookup',
  Has50AmpHookup: 'electric50AmpHookup',
} as const
Object.freeze(AMENITY_LABELS)
export type AMENITY_LABELS = (typeof AMENITY_LABELS)[keyof typeof AMENITY_LABELS]

export const AMENITY_ICONS: Partial<Record<CampgroundAmenity, IconProp>> = {
  HasBeach: ['fal', 'umbrella-beach'],
  HasBoatLaunch: ['fal', 'anchor'],
  HasCableTV: ['fal', 'tv-retro'],
  HasDumpStation: ['fal', 'fill-drip'],
  HasFirewood: ['fal', 'campfire'],
  HasFishing: ['fal', 'fishing-rod'],
  HasGeneralStore: ['fal', 'store'],
  HasGrills: ['fal', 'grill-hot'],
  HasHiking: ['fal', 'person-hiking'],
  HasInternet: ['fal', 'wifi'],
  HasLaundryFacilities: ['fal', 'washing-machine'],
  HasPlayground: ['fal', 'person-sledding'],
  HasPool: ['fal', 'water-ladder'],
  HasPropane: ['fal', 'tank-recovery'],
  HasRVStorage: ['fal', 'warehouse'],
  HasShowers: ['fal', 'shower'],
  HasTables: ['fal', 'table-picnic'],
  HasToilets: ['fal', 'toilet'],
  HasTrails: ['fal', 'route'],
  IsPetFriendly: ['fal', 'paw-simple'],
} as const
Object.freeze(AMENITY_ICONS)
export type AMENITY_ICONS = (typeof AMENITY_ICONS)[keyof typeof AMENITY_ICONS]

export const NIGHTLY_RATES_TO_BOLD_COUNT: Record<NonNullable<Campground['NightlyRates']>, number> = {
  Free: 0,
  Lowest: 1,
  Low: 2,
  Medium: 3,
  High: 4,
  Highest: 5,
} as const
Object.freeze(NIGHTLY_RATES_TO_BOLD_COUNT)
export type NIGHTLY_RATES_TO_BOLD_COUNT = (typeof NIGHTLY_RATES_TO_BOLD_COUNT)[keyof typeof NIGHTLY_RATES_TO_BOLD_COUNT]

export const SITE_DETAILS_AMENITIES: CampgroundAmenity[] = ['HasPullThrusSitesAvailable', 'IsBigRigFriendly', 'HasTentsSites'] as const
Object.freeze(SITE_DETAILS_AMENITIES)
export type SITE_DETAILS_AMENITIES = (typeof SITE_DETAILS_AMENITIES)[keyof typeof SITE_DETAILS_AMENITIES]

export const HOOKUPS_AMENITIES: CampgroundAmenity[] = ['HasWaterHookup', 'HasSewerHookup', 'HasEletricalHookup'] as const
Object.freeze(HOOKUPS_AMENITIES)
export type HOOKUPS_AMENITIES = (typeof HOOKUPS_AMENITIES)[keyof typeof HOOKUPS_AMENITIES]

export const ELECTRIC_HOOKUPS_AMENITIES: CampgroundAmenity[] = ['Has15AmpHookup', 'Has20AmpHookup', 'Has30AmpHookup', 'Has50AmpHookup'] as const
Object.freeze(ELECTRIC_HOOKUPS_AMENITIES)
export type ELECTRIC_HOOKUPS_AMENITIES = (typeof ELECTRIC_HOOKUPS_AMENITIES)[keyof typeof ELECTRIC_HOOKUPS_AMENITIES]

export const CAMPGROUND_PHOTO_ACCEPTED_MIME_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp'] as const
Object.freeze(CAMPGROUND_PHOTO_ACCEPTED_MIME_TYPES)

export const MAX_CAMPGROUND_PHOTO_SIZE_MB = 15
export const MAX_CAMPGROUND_PHOTO_SIZE_BYTES = MAX_CAMPGROUND_PHOTO_SIZE_MB * ONE_MB_IN_BYTES

export const CAMPGROUND_PHOTO_VALIDATION_ERRORS = {
  INVALID_TYPE: 'invalidType',
  TOO_LARGE: 'tooLarge',
} as const
Object.freeze(CAMPGROUND_PHOTO_VALIDATION_ERRORS)
export type CAMPGROUND_PHOTO_VALIDATION_ERRORS = (typeof CAMPGROUND_PHOTO_VALIDATION_ERRORS)[keyof typeof CAMPGROUND_PHOTO_VALIDATION_ERRORS]

/**
 * Namespace shared by Campground fields (useScopedRegle) and the pages that
 * collect them (useCollectScope). Always pass it: without a namespace a
 * collector gathers every scoped instance in the app.
 */
export const VALIDATION_SCOPE_CAMPGROUND = 'campground'
